import { useState, useEffect } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { supplierService, rawMaterialService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import type { Supplier, RawMaterial } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const statusColors: Record<string, "success" | "error"> = {
  active: "success",
  inactive: "error",
};

export default function SuppliersPage() {
  const isOwner = useIsOwner();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");
  const [notes, setNotes] = useState("");
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>([]);
  const [showDetails, setShowDetails] = useState(false);
  const [gstNumber, setGstNumber] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [isMsme, setIsMsme] = useState(false);
  const [msmeNumber, setMsmeNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [suppliesChicks, setSuppliesChicks] = useState(false);
  const [creditDays, setCreditDays] = useState(45);

  const loadData = async () => {
    try {
      const [suppData, matData] = await Promise.all([
        supplierService.list(),
        rawMaterialService.list(),
      ]);
      setSuppliers(suppData as Supplier[]);
      setMaterials(matData as RawMaterial[]);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const materialNameMap = new Map(materials.map((m) => [m.id, m.name]));

  const getSupplierType = (supplier: Supplier): string => {
    const hasMaterials = supplier.material_ids && supplier.material_ids.length > 0;
    const hasChicks = !!supplier.details?.supplies_chicks;
    if (hasMaterials && hasChicks) return "Both";
    if (hasChicks) return "Chicks";
    if (hasMaterials) return "Raw Materials";
    return "-";
  };

  const resetDetails = (details?: Record<string, unknown>) => {
    const d = details || {};
    setGstNumber((d.gst_number as string) || "");
    setPanNumber((d.pan_number as string) || "");
    setIsMsme(!!d.is_msme);
    setMsmeNumber((d.msme_number as string) || "");
    setBankName((d.bank_name as string) || "");
    setAccountNumber((d.account_number as string) || "");
    setIfscCode((d.ifsc_code as string) || "");
    setSuppliesChicks(!!d.supplies_chicks);
    setShowDetails(!!details && Object.values(details).some((v) => v && v !== true));
  };

  const buildDetails = (): Record<string, unknown> | undefined => {
    const d: Record<string, unknown> = {};
    if (gstNumber) d.gst_number = gstNumber;
    if (panNumber) d.pan_number = panNumber;
    if (isMsme) d.is_msme = true;
    if (msmeNumber) d.msme_number = msmeNumber;
    if (bankName) d.bank_name = bankName;
    if (accountNumber) d.account_number = accountNumber;
    if (ifscCode) d.ifsc_code = ifscCode;
    if (suppliesChicks) d.supplies_chicks = true;
    return Object.keys(d).length > 0 ? d : undefined;
  };

  const openCreate = () => {
    setEditing(null);
    setName("");
    setPhone("");
    setAddress("");
    setStatus("active");
    setNotes("");
    setSelectedMaterialIds([]);
    setSuppliesChicks(false);
    setCreditDays(45);
    resetDetails();
    setShowDetails(false);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (supplier: Supplier) => {
    setEditing(supplier);
    setName(supplier.name);
    setPhone(supplier.phone ?? "");
    setAddress(supplier.address ?? "");
    setStatus(supplier.status);
    setNotes(supplier.notes ?? "");
    setSelectedMaterialIds(supplier.material_ids ?? []);
    setSuppliesChicks(!!supplier.details?.supplies_chicks);
    setCreditDays(supplier.credit_days ?? 45);
    resetDetails(supplier.details);
    setError("");
    setModalOpen(true);
  };

  const toggleMaterial = (id: number) => {
    setSelectedMaterialIds((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    const payload: Partial<Supplier> & { material_ids: number[]; credit_days: number } = {
      name,
      phone: phone || undefined,
      address: address || undefined,
      status,
      notes: notes || undefined,
      details: buildDetails(),
      credit_days: creditDays,
      material_ids: selectedMaterialIds,
    };

    try {
      if (editing) {
        await supplierService.update(editing.id, payload);
      } else {
        await supplierService.create(payload);
      }
      setModalOpen(false);
      setLoading(true);
      await loadData();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageMeta title="Suppliers | Poultry Book" description="Manage suppliers" />
      <PageBreadcrumb pageTitle="Suppliers" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            All Suppliers
          </h3>
          {isOwner && (
            <Button size="sm" onClick={openCreate}>
              Add Supplier
            </Button>
          )}
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : suppliers.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No suppliers found. Add your first supplier.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Name
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Phone
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Type
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Materials
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </TableCell>
                  {isOwner && (
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((supplier) => (
                  <TableRow key={supplier.id} className="border-t border-gray-100 dark:border-gray-800">
                    <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                      {supplier.name}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {supplier.phone ?? "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {getSupplierType(supplier)}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      {supplier.material_ids && supplier.material_ids.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {supplier.material_ids.map((mid) => (
                            <span
                              key={mid}
                              className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                            >
                              {materialNameMap.get(mid) ?? `#${mid}`}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      <Badge size="sm" color={statusColors[supplier.status] ?? "light"}>
                        {supplier.status}
                      </Badge>
                    </TableCell>
                    {isOwner && (
                      <TableCell className="px-5 py-3 text-right">
                        <button
                          onClick={() => openEdit(supplier)}
                          className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                        >
                          Edit
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-md p-5 sm:p-8 mx-4">
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Supplier" : "Add Supplier"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="supplierName">Name *</Label>
            <Input
              id="supplierName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. ABC Feeds Ltd"
              required
              maxLength={100}
            />
          </div>

          <div>
            <Label htmlFor="supplierPhone">Phone</Label>
            <Input
              id="supplierPhone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 9876543210"
              maxLength={20}
            />
          </div>

          <div>
            <Label htmlFor="supplierAddress">Address</Label>
            <textarea
              id="supplierAddress"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Physical address"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          <div>
            <Label>Supplier Type</Label>
            <label className="flex cursor-pointer items-center gap-2 mt-1 mb-2 px-1">
              <input
                type="checkbox"
                checked={suppliesChicks}
                onChange={(e) => setSuppliesChicks(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Supplies Chicks</span>
            </label>
          </div>

          <div>
            <Label>Raw Materials Supplied</Label>
            {materials.length === 0 ? (
              <p className="text-xs text-gray-400">No raw materials defined yet.</p>
            ) : (
              <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-gray-300 p-2 dark:border-gray-700">
                {materials.map((m) => (
                  <label
                    key={m.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMaterialIds.includes(m.id)}
                      onChange={() => toggleMaterial(m.id)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300">{m.name}</span>
                    <span className="text-xs text-gray-400">({m.unit})</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="supplierCreditDays">Credit Days</Label>
            <Input
              id="supplierCreditDays"
              type="number"
              value={creditDays}
              onChange={(e) => setCreditDays(parseInt(e.target.value) || 0)}
              placeholder="45"
              min={0}
              max={365}
            />
            <p className="mt-1 text-xs text-gray-400">Default payment period in days</p>
          </div>

          <div>
            <Label htmlFor="supplierStatus">Status *</Label>
            <select
              id="supplierStatus"
              value={status}
              onChange={(e) => setStatus(e.target.value as "active" | "inactive")}
              className={selectClass}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <Label htmlFor="supplierNotes">Notes</Label>
            <textarea
              id="supplierNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          {/* Collapsible Details */}
          <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setShowDetails(!showDetails)}
              className="flex w-full items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg
                className={`h-4 w-4 transition-transform ${showDetails ? "rotate-90" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Business &amp; Bank Details
            </button>

            {showDetails && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="supplierGst">GST Number</Label>
                    <Input
                      id="supplierGst"
                      value={gstNumber}
                      onChange={(e) => setGstNumber(e.target.value)}
                      placeholder="e.g. 27AABCU9603R1ZM"
                    />
                  </div>
                  <div>
                    <Label htmlFor="supplierPan">PAN Number</Label>
                    <Input
                      id="supplierPan"
                      value={panNumber}
                      onChange={(e) => setPanNumber(e.target.value)}
                      placeholder="e.g. AABCU9603R"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={isMsme}
                      onChange={(e) => setIsMsme(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300">MSME Registered</span>
                  </label>
                  {isMsme && (
                    <div className="flex-1">
                      <Input
                        value={msmeNumber}
                        onChange={(e) => setMsmeNumber(e.target.value)}
                        placeholder="MSME Number"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="supplierBank">Bank Name</Label>
                  <Input
                    id="supplierBank"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="e.g. State Bank of India"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="supplierAccount">Account Number</Label>
                    <Input
                      id="supplierAccount"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      placeholder="e.g. 1234567890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="supplierIfsc">IFSC Code</Label>
                    <Input
                      id="supplierIfsc"
                      value={ifscCode}
                      onChange={(e) => setIfscCode(e.target.value)}
                      placeholder="e.g. SBIN0001234"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !name}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
